import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
} from '@dynamic-labs-sdk/client/core';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';
import { Transaction } from '@mysten/sui/transactions';

import type { SuiSigningPayload } from '../../checkoutTransaction.types';
import { isSuiWalletAccount } from '../../isSuiWalletAccount';
import { isSuiWalletProvider } from '../../isSuiWalletProvider';

/**
 * Executes a Sui checkout transaction by deserializing the base64-encoded
 * BCS transaction and sending it via the wallet provider's signAndExecuteTransaction.
 *
 * 1. Validates that the wallet account is a Sui wallet account
 * 2. Extracts and validates the serialized transaction from the signing payload
 * 3. Deserializes the transaction using Transaction.from()
 * 4. Signs and executes via the Sui wallet provider
 *
 * @param params.signingPayload - The signing payload containing `serializedTransaction`.
 * @param params.walletAccount - The wallet account used to sign and execute the transaction.
 * @param params.onStepChange - Optional callback invoked with `'transaction'` when the execution step begins.
 * @param client - The Dynamic client instance.
 * @returns The transaction hash (digest).
 */
export const suiExecuteCheckoutTransaction = async (
  {
    onStepChange,
    signingPayload,
    walletAccount,
  }: ExecuteCheckoutTransactionParams,
  client: DynamicClient
): Promise<ExecuteCheckoutTransactionResult> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (
    !isSuiWalletAccount(walletAccount) ||
    !isSuiWalletProvider(walletProvider)
  ) {
    throw new InvalidParamError(
      'Wallet account is not a Sui wallet account'
    );
  }

  const suiPayload = signingPayload as SuiSigningPayload;

  if (!suiPayload.serializedTransaction?.serializedTransaction) {
    throw new InvalidParamError(
      'Signing payload is missing serialized transaction data'
    );
  }

  const { serializedTransaction: serializedTransactionStr } =
    suiPayload.serializedTransaction;

  const transaction = Transaction.from(serializedTransactionStr);

  onStepChange?.('transaction');

  const result = await walletProvider.signAndExecuteTransaction({
    transaction,
    walletAccount,
  });

  return { transactionHash: result.digest };
};
